import moment from "moment";

/**
 * Habit Save withdrawal windows, as dates.
 *
 * The form, its review and the fund preview all show the windows a customer
 * would get. They used to each work the dates out for themselves; this is the
 * one place now, so the three cannot disagree.
 */

/** `Enums.Frequency` — how a withdrawal window's interval is counted. */
export const WindowUnit = {
  Daily: 1,
  Weekly: 2,
  Monthly: 3,
} as const;

/** What an interval of each unit is counted in, e.g. "6 months". */
export const WINDOW_UNIT_NAME: Record<number, string> = {
  [WindowUnit.Daily]: "days",
  [WindowUnit.Weekly]: "weeks",
  [WindowUnit.Monthly]: "months",
};

/** How many windows the screens show. */
export const WINDOWS_SHOWN = 3;

const MOMENT_UNIT: Record<number, moment.unitOfTime.DurationConstructor> = {
  [WindowUnit.Daily]: "days",
  [WindowUnit.Weekly]: "weeks",
  [WindowUnit.Monthly]: "months",
};

export type TWindowDates = { opens: moment.Moment; closes: moment.Moment };

/**
 * The first windows for a plan opened on `start`.
 *
 * Empty when there is no interval to count by. Each window covers `grace`
 * days counting from the day it opens.
 */
export function planWindows({
  start,
  interval,
  unit,
  grace,
  count = WINDOWS_SHOWN,
}: {
  start: moment.Moment;
  interval?: number | null;
  unit?: number | null;
  grace?: number | null;
  count?: number;
}): TWindowDates[] {
  if (!interval || interval <= 0) {
    return [];
  }

  const step = MOMENT_UNIT[unit ?? WindowUnit.Monthly] ?? "months";

  return Array.from({ length: count }, (_unused, i) => {
    const opens = start.clone().add(interval * (i + 1), step);

    return {
      opens,
      closes: opens.clone().add(Math.max((grace ?? 1) - 1, 0), "days"),
    };
  });
}

/** "1 Apr 2027", "1 – 3 Apr 2027" or "30 Mar – 1 Apr 2027". */
export function formatWindowRange({ opens, closes }: TWindowDates): string {
  if (opens.isSame(closes, "day")) {
    return opens.format("D MMM YYYY");
  }

  return opens.isSame(closes, "month")
    ? `${opens.format("D")} – ${closes.format("D MMM YYYY")}`
    : `${opens.format("D MMM")} – ${closes.format("D MMM YYYY")}`;
}
